"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";

import { cn } from "@/lib/utils";

type CheckboxProps = React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  indeterminate?: boolean;
};

function Checkbox({ className, indeterminate, ...props }: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      data-indeterminate={indeterminate || false}
      className={cn(
        // Base layout
        "peer flex items-center justify-center w-[18px] h-[18px] shrink-0 rounded-[4px] border-[1.5px] outline-none transition-shadow",
        // Default/off state
        "border-gray-500 bg-white",
        // Checkbox icon color default
        "text-gray-500",
        // Hover
        "hover:border-blue-200 hover:ring-blue-200 hover:text-blue-200",
        // Show tick/icon on hover (even when not checked)
        "group relative",
        // Focus
        "focus-visible:border-blue-500 focus-visible:ring-blue-500",
        // Checked state
        "data-[state=checked]:border-blue-500 data-[state=checked]:text-blue-500",
        // Checked+hover
        "data-[state=checked]:hover:border-blue-500",
        // Disabled
        "disabled:cursor-not-allowed disabled:border-gray-500 disabled:bg-gray-300 disabled:text-gray-500",
        // Checked+disabled
        "data-[state=checked]:disabled:border-gray-500 data-[state=checked]:disabled:bg-gray-50 data-[state=checked]:disabled:text-gray-500",
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
      >
        {indeterminate === undefined ? (
          <CheckIcon strokeWidth={3.5} className="size-3" />
        ) : indeterminate ? (
          ""
        ) : (
          <CheckIcon strokeWidth={3.5} className="size-3" />
        )}
      </CheckboxPrimitive.Indicator>

      <span
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center",
          "opacity-0 group-hover:opacity-100",
          "transition-opacity duration-150",
          "data-[state=checked]:opacity-0 data-[state=indeterminate]:opacity-0"
        )}
      >
        <CheckIcon strokeWidth={3.5} className="size-3 text-blue-200" />
      </span>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
